/* eslint-disable @typescript-eslint/no-extra-semi */
import { Preferences } from '@capacitor/preferences'
import { ModalOption } from '@vae/server/src/types/notifications'
import React, { createContext, useContext, useState } from 'react'
import { clearTokens } from '../authentication/utilities'
import { trpc } from '../modules/trpc'
import usePreference from '../utils/preferences/usePreference'
import { AppContext } from './AppContext'
import useUser from './hooks/useUser'
import { useUserInfoUpdater } from './hooks/useUserInfoUpdater'
import { ModalContext } from './ModalContext'

// import { Capacitor } from '@capacitor/core'

interface StrongAuthenticationDetails {
	firstNames: string
	surName: string
	personalId: string
}

interface UserContextType {
	logOut: () => void
	loggedIn: boolean
	strongAuthenticationDetails: StrongAuthenticationDetails | null
	setStrongAuthenticationDetails: React.Dispatch<
		React.SetStateAction<StrongAuthenticationDetails | null>
	>
	setLoggedIn: React.Dispatch<React.SetStateAction<boolean>>
	skipLogin: boolean
	setSkipLogin: (value: boolean) => void
	isUserContextLoading: boolean
	isAnonymousUser: boolean
	postAuthProductId?: string | null
	postAuthRedirect?: ModalOption | null
	setPostAuthRedirect: (newValue: ModalOption | null) => void
	setPostAuthProductId: (value: string | null) => void
	authExpire?: string | null
	setAuthExpire: (value: string | null) => void
}

export const UserContext = createContext({} as UserContextType)

interface Props {
	children: React.ReactNode
}

const UserContextProvider: React.FC<Props> = ({ children }) => {
	const { menuOpen, toggleMenu } = useContext(AppContext)
	const { clearModals } = useContext(ModalContext)

	const [strongAuthenticationDetails, setStrongAuthenticationDetails] =
		useState<StrongAuthenticationDetails | null>(null)
	const [loggedIn, setLoggedIn] = useState(false)

	const { user, isLoading } = useUser()

	const [skipLogin, setSkipLogin, isSkipLoginLoading] = usePreference('skipLogin', false)
	const [postAuthRedirect, setPostAuthRedirect, isPostAuthRedirectLoading] = usePreference(
		'postAuthRedirect',
		null
	)
	const [postAuthProductId, setPostAuthProductId, isProductIdLoading] = usePreference(
		'postAuthProductId',
		null
	)
	const [authExpire, setAuthExpire, isAuthLoading] = usePreference('authExpire', null)

	useUserInfoUpdater()

	const utils = trpc.useContext()

	trpc.achievements.checkAchievementByType.useQuery(
		{ userId: user?.id ?? 0, type: 'consumptionEffect' },
		{
			enabled: !!user?.id,
			onSuccess: () => {
				if (user?.id) {
					utils.achievements.getUserAchievements.invalidate(user.id)
				}
			},
			refetchInterval: 5 * 60 * 1000,
		}
	)

	const logOut = async () => {
		try {
			await clearTokens()
			setLoggedIn(false)
			setSkipLogin(false)

			// Preferences.clear()
			clearModals()
			utils.user.currentUser.invalidate()

			Preferences.remove({ key: 'postAuthRedirect' })
			Preferences.remove({ key: 'postAuthProductId' })
			Preferences.remove({ key: 'authExpire' })
			if (menuOpen) {
				toggleMenu()
			}
			window.location.href = '/'
		} catch (error) {
			console.error('Error during logout:', error)
		}
	}

	return (
		<UserContext.Provider
			value={{
				logOut,
				loggedIn,
				setLoggedIn,
				skipLogin,
				setSkipLogin,
				isUserContextLoading:
					isSkipLoginLoading ||
					isPostAuthRedirectLoading ||
					isProductIdLoading ||
					isAuthLoading ||
					isLoading,
				strongAuthenticationDetails,
				setStrongAuthenticationDetails,
				isAnonymousUser: skipLogin,
				postAuthRedirect,
				postAuthProductId,
				setPostAuthRedirect,
				setPostAuthProductId,
				authExpire,
				setAuthExpire,
			}}
		>
			{children}
		</UserContext.Provider>
	)
}

export default UserContextProvider
